<template>
  <div id="AccountAgency" class="Main-Content">

    <LoadingLayout v-if="loading" :page="true" />

    <Page
      v-if="!loading"
      :title="$t('Agency')"
    >

      <Layout v-if="cannotAccess">
        <LayoutSection>
          <Card>
            <CardSection :title="$t('No access')" class="ErrorText">
              <Icon icon="fas fas fa-exclamation-triangle fa-2x" />
              <p>{{$t('AccountAgency.noAccess')}}</p>
            </CardSection>
          </Card>
        </LayoutSection>
      </Layout>

      <template v-else-if="theUser.isAgency">
        <Layout>
          <LayoutSection>
            <Card :title="$t('Instructions')">
              <CardSection>
                <p>{{$t('AccountAgency.instructionsHelp')}}</p>
                <ul>
                  <li>{{$t('Visit')}} <a href="https://portal.fanplayr.com/#/account/agency/">https://portal.fanplayr.com/#/account/agency/</a></li>
                  <li v-if="theUser.isSuper">{{$t('AccountAgency.enterAgencyEmailSuper', { email: `${theUser.email}` })}}</li>
                  <li v-else>{{$t('AccountAgency.enterAgencyEmail')}}</li>
                </ul>
              </CardSection>
            </Card>

            <Card :title="$t('AccountAgency.pendingMerchantRequests')">
              <CardSection>
                <div v-if="accountsPending && accountsPending.length > 0">
                  <div v-for="a in accountsPending" :key="a.id" class="agency-merchant pending">
                    <div class="name">{{ a.name }}</div>
                    <ButtonGroup>
                      <Button type="destructive" @click="rejectAgencyRequest(a.linkId)" :disabled="saving">{{$t('Decline')}}</Button>
                      <Button type="primary" @click="acceptAgencyRequest(a.linkId)" :disabled="saving">{{$t('Accept')}}</Button>
                    </ButtonGroup>
                  </div>
                </div>
                <div v-else>
                  <p>{{$t('AccountAgency.noPendingRequestsMessage')}}</p>
                </div>
              </CardSection>
            </Card>

            <Card :title="$t('AccountAgency.activeMerchants')">
              <CardSection>
                <div v-if="accountsActive.length > 0">
                  <div v-for="a in accountsActive" :key="a.id" class="agency-merchant">
                    <div class="name">{{ a.name }}</div>
                    <ButtonGroup>
                      <Button type="primary" @click="rejectAgencyRequest(a.linkId)" :disabled="saving">{{$t('Remove')}}</Button>
                      <Button class="btn btn-default btn-sm" @click="switchAccount(a.id)" :disabled="saving">{{$t('AccountAgency.manageAccount')}}</Button>
                    </ButtonGroup>
                  </div>
                </div>
                <div v-else>
                  <p style="padding-left: 20px;">{{$t('AccountAgency.noMerchantAccountsMessage')}}</p>
                </div>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>
      </template>

      <template v-else-if="agencyDetails">

        <Card :title="$t('AccountAgency.agencyRequestSent')" v-if="agencyDetails.status === 'pending'">
          <CardSection>
              <p>{{$t('AccountAgency.agencyRequest', { agency: agencyDetails.name })}}</p>
              <p>{{$t('AccountAgency.cancelRequestMessage')}}</p>
          </CardSection>
          <CardSection>
            <Button @click="removeAgency" :disabled="saving" type="destructive">{{$t('AccountAgency.cancelRequest')}}</Button>
          </CardSection>
        </Card>

        <Card v-else>
          <CardSection>
            <p>{{$t('AccountAgency.grantedAgencyMessage', { agency: agencyDetails.name })}}</p>
            <p>{{$t('AccountAgency.revokeAccessMessage')}}</p>
          </CardSection>
          <CardSection>
            <Button type="destructive" @click="removeAgency" :disabled="saving">{{$t('AccountAgency.revokeAgencyAccess')}}</Button>
          </CardSection>
        </Card>
      </template>

      <template v-else>

        <Card>
          <CardSection :title="$t('AccountAgency.addAgency')">
            <p>{{$t('AccountAgency.agencyEmailAddressMessage')}}</p>
            <validation-provider :name="$t('Email')" immediate rules="email" v-slot="{ errors }">
              <TextField v-model="newAgencyEmail" :label="$t('AccountAgency.agencyEmail')" /> 
              <span class="error">{{ errors[0] }}</span>
            </validation-provider>
          </CardSection>
          <CardSection>
            <Button type="primary" @click="requestAgency" :disabled="saving">{{$t('AccountAgency.addAgency')}}</Button>
          </CardSection>
        </Card>

      </template>

    </Page>

  </div>
</template>

<script>

import { ValidationProvider } from 'vee-validate/dist/vee-validate.full'

export default {
  name: 'AccountAgency',
  data () {
    return {
      loading: true,
      saving: false,
      // agent
      accountsPending: [],
      accountsActive: [],
      // non agent
      agencyDetails: null,
      //
      newAgencyEmail: ''
    }
  },
  mounted() {
    // doesn't need any data
    if (this.cannotAccess) {
      this.loading = false
      return
    }

    NProgress.start()

    if (this.theUser.isAgency) {
      this.api.getAccountsForAgency()
      .then((response) => {
        this.accountsPending = response.pending
        this.accountsActive = response.active

        this.loading = false

        this.checkForAccept()
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        NProgress.done()
      })
    } else {
      this.api.getAgencyAccountDetails()
      .then((response) => {
        this.agencyDetails = response
        this.loading = false
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        NProgress.done()
      })
    }
  },
  computed: {
    cannotAccess() {
      return this.theUser.isAgency &&
        !this.theUser.isSuper &&
        this.theUser.accountId != this.theUser.onBehalfOfAccountId
    }
  },
  methods: {
    checkForAccept() {
      if (this.$route.params.id) {
        const linkId = this.$route.params.id
        const accept = this.$route.params.accept === '1'
        if (accept) {
          this.acceptAgencyRequest(linkId)
        } else {
          this.rejectAgencyRequest(linkId)
        }
      }
    },
    acceptAgencyRequest(linkId) {
      NProgress.start()
      this.saving = true
      this.api.acceptAgencyRequest(linkId)
      .then(() => {
        let acceptedAccount = this.accountsPending.splice(this.accountsPending.findIndex(acc => acc.linkId === linkId), 1)
        this.accountsActive.unshift(acceptedAccount)
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        this.saving = false
        NProgress.done()
      })
    },
    rejectAgencyRequest(linkId) {
      NProgress.start()
      this.saving = true
      this.api.rejectAgencyRequest(linkId)
      .then(() => {
        this.accountsPending.splice(this.accountsPending.findIndex(acc => acc.linkId === linkId), 1)
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        this.saving = false
        NProgress.done()
      })
    },
    requestAgency() {
      NProgress.start()
      this.saving = true
      this.api.requestAgencyAuthorizationByEmail(this.newAgencyEmail)
      .then((agencyDetails) => {
        this.agencyDetails = agencyDetails
        this.$ui.alert({
          title: this.$t('Alert'),
          content: 'request.sent'
        })
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        this.saving = false
        NProgress.done()
      })
    },
    removeAgency() {
      NProgress.start()
      this.saving = true
      this.api.removeAgency()
      .then(() => {
        this.agencyDetails = null
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        this.saving = false
        NProgress.done()
      })
    },
    async switchAccount(accountId) {
      NProgress.start()
      this.api.switchAccount(accountId)
      .then(async () => {
        await this.doRefreshToken()
        this.$router.push('/account/profile')

        // tell other windows to refresh
        if (this.refreshChannel) {
          this.refreshChannel.postMessage({
            hard: false
          })
        }
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
    }
  },
  components: {
    ValidationProvider
  }
}

</script>


<style lang="scss">
.agency-merchant {
  margin-bottom: 15px;
  .name {
    margin-bottom: 5px;
    font-size: 120%;
  }
}
</style>